import {
  PLAYER_COLORS,
  type ClientRoomState,
} from "@poppycock/shared";
import { motion } from "framer-motion";
import { useCallback, useRef, useState, type ReactNode } from "react";
import { useAutoSubmitOnTimeout } from "../hooks/useAutoSubmitOnTimeout";
import { CaptionPrompt } from "./CaptionPrompt";
import { AnswerBody } from "./DesignStage";
import { PhaseTimer } from "./PhaseTimer";
import { PlayerList } from "./PlayerList";
import { Button, Card, PhaseBadge, ProgressDots } from "./ui";

export function RevealScreen({
  state,
  isHost,
  onContinue,
}: {
  state: ClientRoomState;
  isHost: boolean;
  onContinue: () => void;
}) {
  const round = state.round!;

  return (
    <div className="flex flex-1 flex-col">
      <header className="mb-5 flex items-center justify-between">
        <div>
          <p className="text-xs tracking-wide text-[var(--color-muted)] uppercase">
            Round {round.number}
          </p>
          <h2 className="text-xl font-semibold tracking-tight">
            The reveal
          </h2>
        </div>
        <PhaseBadge phase="reveal" />
      </header>

      <Card className="mb-4">
        {round.kind === "caption" && round.imageId ? (
          <CaptionPrompt
            imageId={round.imageId}
            category={round.category}
            compact
          />
        ) : (
          <p className="text-sm text-[var(--color-muted)]">{round.category}</p>
        )}
      </Card>

      <ul className="mb-4 flex flex-1 flex-col gap-3 overflow-y-auto">
        {round.answers.map((a, i) => {
          const author = state.players.find((p) => p.id === a.authorId);
          const color = author
            ? PLAYER_COLORS[author.colorIndex % PLAYER_COLORS.length]
            : "#888";
          return (
            <motion.li
              key={a.id}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.08 }}
              className="glass rounded-2xl p-4"
            >
              <div className="mb-3">
                <AnswerBody answer={a} />
              </div>
              <div className="flex items-center gap-2">
                <span
                  className="rounded-full px-2.5 py-0.5 text-xs font-bold text-[#141414]"
                  style={{ background: color }}
                >
                  {a.authorName ?? "???"}
                </span>
              </div>
            </motion.li>
          );
        })}
      </ul>

      <Card className="mb-4">
        <h3 className="mb-3 text-xs font-semibold tracking-wide text-[var(--color-muted)] uppercase">
          Scoreboard
        </h3>
        <PlayerList
          players={state.players}
          hostId={state.hostId}
          youId={state.you.id}
          showScores
          lastDelta={state.lastScoreDelta}
        />
      </Card>

      {isHost ? (
        <Button className="w-full" onClick={onContinue}>
          Vote for funniest →
        </Button>
      ) : (
        <p className="text-center text-sm text-[var(--color-muted)]">
          Waiting for host…
        </p>
      )}
    </div>
  );
}

export function FunniestScreen({
  state,
  isHost,
  onVote,
  onSkip,
}: {
  state: ClientRoomState;
  isHost: boolean;
  onVote: (answerId: string) => void;
  onSkip: () => void;
}) {
  const round = state.round!;
  const submitted = round.youSubmitted;
  const [picked, setPicked] = useState<string | null>(
    round.yourFunniestVote,
  );
  const pickedRef = useRef(picked);
  pickedRef.current = picked;

  // Can't vote for self
  const votable = round.answers.filter((a) => a.authorId !== state.you.id);

  const forceSubmit = useCallback(() => {
    // Ship the selection if they made one; otherwise abstain — server ends the phase.
    const choice = pickedRef.current;
    if (choice) onVote(choice);
  }, [onVote]);

  const countdown = useAutoSubmitOnTimeout(
    state.phaseEndsAt,
    submitted,
    forceSubmit,
  );

  if (submitted) {
    return (
      <div className="flex flex-1 flex-col">
        <FunniestHeader round={round.number} />
        <Card className="flex flex-1 flex-col items-center justify-center py-12 text-center">
          <p className="mb-2 text-lg font-semibold">Vote cast</p>
          <ProgressDots
            submitted={round.submittedCount}
            total={round.totalPlayers}
          />
          {isHost && (
            <Button className="mt-8" variant="secondary" onClick={onSkip}>
              End voting early
            </Button>
          )}
        </Card>
      </div>
    );
  }

  return (
    <div className="flex flex-1 flex-col">
      <FunniestHeader
        round={round.number}
        timer={
          <PhaseTimer
            label={countdown.label}
            urgency={countdown.urgency}
            active={countdown.active}
          />
        }
      />

      {round.kind === "caption" && round.imageId && (
        <Card className="mb-4">
          <CaptionPrompt
            imageId={round.imageId}
            category={round.category}
            compact
          />
        </Card>
      )}

      <p className="mb-4 text-sm text-[var(--color-muted)]">
        {round.kind === "design"
          ? "Which drawing actually made you snort? (Not your own. We know.)"
          : round.kind === "caption"
            ? "Which caption actually made you snort? (Not your own. We know.)"
            : "Which answer actually made you snort? (Not your own. We know.)"}
      </p>

      <ul className="mb-4 flex flex-1 flex-col gap-3 overflow-y-auto">
        {votable.map((a) => {
          const active = picked === a.id;
          return (
            <button
              key={a.id}
              type="button"
              onClick={() => setPicked(a.id)}
              className={`glass rounded-2xl p-4 text-left transition ${
                active ? "ring-2 ring-[var(--color-accent)]" : ""
              }`}
            >
              <AnswerBody answer={a} />
              <p className="mt-2 text-xs text-[var(--color-muted)]">
                by {a.authorName}
              </p>
            </button>
          );
        })}
      </ul>

      <Button
        className="w-full"
        disabled={!picked}
        onClick={() => picked && onVote(picked)}
      >
        Vote funniest
      </Button>
    </div>
  );
}

export function RoundEndScreen({
  state,
  isHost,
  onNext,
  onEndGame,
  onLobby,
}: {
  state: ClientRoomState;
  isHost: boolean;
  onNext: () => void;
  onEndGame: () => void;
  onLobby: () => void;
}) {
  const round = state.round!;
  const tallies = round.funniestTallies ?? [];
  const maxVotes = Math.max(0, ...tallies.map((t) => t.votes));
  const winners = new Set(
    tallies.filter((t) => t.votes === maxVotes && maxVotes > 0).map((t) => t.answerId),
  );

  return (
    <div className="flex flex-1 flex-col">
      <header className="mb-5 flex items-center justify-between">
        <div>
          <p className="text-xs tracking-wide text-[var(--color-muted)] uppercase">
            Round {round.number} complete
          </p>
          <h2 className="text-xl font-semibold tracking-tight">
            Standings
          </h2>
        </div>
        <PhaseBadge phase="round_end" />
      </header>

      {maxVotes > 0 && (
        <Card strong className="mb-4">
          <p className="mb-2 text-xs tracking-wide text-[var(--color-accent-soft)] uppercase">
            Funniest this round
          </p>
          <ul className="flex flex-col gap-2">
            {round.answers
              .filter((a) => winners.has(a.id))
              .map((a) => (
                <li key={a.id}>
                  <AnswerBody answer={a} className="mb-1" />
                  <p className="text-sm text-[var(--color-muted)]">
                    {a.authorName} · {tallies.find((t) => t.answerId === a.id)?.votes ?? 0}{" "}
                    votes
                  </p>
                </li>
              ))}
          </ul>
        </Card>
      )}

      <Card className="mb-6 flex-1">
        <PlayerList
          players={state.players}
          hostId={state.hostId}
          youId={state.you.id}
          showScores
          lastDelta={state.lastScoreDelta}
        />
      </Card>

      {isHost ? (
        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-3 sm:flex-row">
            <Button className="flex-1" onClick={onNext}>
              Next round
            </Button>
            <Button className="flex-1" variant="secondary" onClick={onEndGame}>
              End game · crown winners
            </Button>
          </div>
          <Button variant="ghost" onClick={onLobby}>
            Back to lobby
          </Button>
        </div>
      ) : (
        <p className="text-center text-sm text-[var(--color-muted)]">
          Waiting for host to continue…
        </p>
      )}
    </div>
  );
}

function FunniestHeader({
  round,
  timer,
}: {
  round: number;
  timer?: ReactNode;
}) {
  return (
    <header className="mb-5 flex flex-wrap items-center justify-between gap-3">
      <div>
        <p className="text-xs tracking-wide text-[var(--color-muted)] uppercase">
          Round {round}
        </p>
        <h2 className="text-xl font-semibold tracking-tight">
          Funniest entry
        </h2>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {timer}
        <PhaseBadge phase="funniest" />
      </div>
    </header>
  );
}
